import React from "react";

const fmtUa = (v) =>
    Number(Math.round(Number(v) || 0)).toLocaleString("uk-UA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

const SOURCE_LABELS = [
    { k: "instagram", icon: "📷", title: "Instagram" },
    { k: "site", icon: "🌐", title: "Сайт" },
    { k: "showroom", icon: "🏪", title: "Шоурум" },
];

function showOtherColumnForRows(rows) {
    return rows.some((r) => (r.bySource?.other?.orders ?? 0) > 0);
}

function SourceHeaderCells({ rows }) {
    const showOther = showOtherColumnForRows(rows);
    return (
        <>
            {SOURCE_LABELS.map((s) => (
                <th key={s.k} className="text-end text-nowrap">
                    <span title={`${s.title}: замовл. / ТО`}>
                        {s.icon} зам/ТО
                    </span>
                </th>
            ))}
            {showOther ? (
                <th className="text-end text-nowrap" title="Інші джерела: замовл. / ТО">
                    Інше зам/ТО
                </th>
            ) : null}
        </>
    );
}

function SourceDataCells({ row, rowsForOtherCheck }) {
    const showOther = showOtherColumnForRows(rowsForOtherCheck);
    return (
        <>
            {SOURCE_LABELS.map((s) => {
                const b = row.bySource?.[s.k] || { orders: 0, turnover: 0 };
                return (
                    <td key={s.k} className="text-end small text-nowrap">
                        <span className="text-body">{b.orders}</span>
                        <span className="text-muted"> / </span>
                        <span className="text-body">{fmtUa(b.turnover)}</span>
                    </td>
                );
            })}
            {showOther ? (
                <td className="text-end small text-nowrap">
                    <span className="text-body">{row.bySource?.other?.orders ?? 0}</span>
                    <span className="text-muted"> / </span>
                    <span className="text-body">{fmtUa(row.bySource?.other?.turnover ?? 0)}</span>
                </td>
            ) : null}
        </>
    );
}

function ScheduleGraphicCells({ reactKey, statsMap, status }) {
    const dash = (
        <td className="text-end">
            <span className="text-muted">—</span>
        </td>
    );
    if (status === "loading") {
        return (
            <>
                <td className="text-end">
                    <span className="text-muted">…</span>
                </td>
                <td className="text-end">
                    <span className="text-muted">…</span>
                </td>
            </>
        );
    }
    if (status === "error") {
        return (
            <>
                <td className="text-end" title="Не вдалося завантажити графік">
                    <span className="text-muted">—</span>
                </td>
                {dash}
            </>
        );
    }
    const st = statsMap?.[reactKey];
    if (st == null || typeof st !== "object") {
        return (
            <>
                {dash}
                {dash}
            </>
        );
    }
    return (
        <>
            <td className="text-end" title="/schedule до сьогодні включно по Києву у межах періоду звіту">
                {st.workedThroughToday}
            </td>
            <td className="text-end" title="Усі дні періоду звіту з відміткою «зміна» за /schedule">
                {st.totalInReportRange}
            </td>
        </>
    );
}

/**
 * @param {Array} [rows]
 * @param {Array} [offlineRows]
 * @param {Record<string, { workedThroughToday: number, totalInReportRange: number }|null>} [scheduleShiftStatsByReactKey]
 * @param {"idle"|"loading"|"ok"|"error"} [scheduleDaysLoadStatus]
 * @param {boolean} [showScheduleWorkingDaysColumn]
 */
export default function DirectManagerDialogConversion({
    rows,
    offlineRows,
    scheduleShiftStatsByReactKey = {},
    scheduleDaysLoadStatus = "idle",
    showScheduleWorkingDaysColumn = false,
}) {
    const allForBySource = [...(rows || []), ...(offlineRows || [])];

    const unified = [
        ...(rows || []).map((r) => ({
            reactKey: `direct-${r.key}`,
            category: "Директ",
            managerLabel: r.directLabel,
            directShifts: r.directShifts,
            dialogs: r.dialogs,
            sales: r.sales,
            percent: r.percent,
            turnover: r.turnover,
            avgCheck: r.avgCheck,
            bySource: r.bySource,
        })),
        ...(offlineRows || []).map((r) => ({
            reactKey: `offline-${r.key}`,
            category: "Офлайн",
            managerLabel: r.displayLabel,
            directShifts: null,
            dialogs: null,
            sales: r.sales,
            percent: null,
            turnover: r.turnover,
            avgCheck: r.avgCheck,
            bySource: r.bySource,
        })),
    ];

    if (unified.length === 0) return null;

    return (
        <div className="rounded border bg-light p-3 mb-3">
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle mb-0 small">
                    <thead className="table-light">
                        <tr>
                            <th className="text-nowrap">Категорія</th>
                            <th>Менеджер</th>
                            {showScheduleWorkingDaysColumn ? (
                                <>
                                    <th
                                        className="text-end text-nowrap"
                                        title="/schedule: зміни від початку періоду до сьогодні включно (Київ)"
                                    >
                                        Відпрацьовано
                                    </th>
                                    <th
                                        className="text-end text-nowrap"
                                        title="/schedule: усі зміни в межах повного періоду звіту"
                                    >
                                        За період
                                    </th>
                                </>
                            ) : null}
                            <th className="text-end text-nowrap">Дні Директ</th>
                            <th className="text-end">Діалоги</th>
                            <th className="text-end">Замовл.</th>
                            <th className="text-end">Конверсія</th>
                            <th className="text-end text-nowrap">ТО, грн</th>
                            <th className="text-end text-nowrap">Сер. чек</th>
                            <SourceHeaderCells rows={allForBySource} />
                        </tr>
                    </thead>
                    <tbody>
                        {unified.map((r) => (
                            <tr key={r.reactKey}>
                                <td className="text-nowrap">{r.category}</td>
                                <td className="fw-medium">{r.managerLabel}</td>
                                {showScheduleWorkingDaysColumn ? (
                                    <ScheduleGraphicCells
                                        reactKey={r.reactKey}
                                        statsMap={scheduleShiftStatsByReactKey}
                                        status={scheduleDaysLoadStatus}
                                    />
                                ) : null}
                                <td className="text-end">
                                    {r.directShifts != null ? r.directShifts : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td className="text-end">
                                    {r.dialogs != null ? r.dialogs : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-end">{r.sales}</td>
                                <td className="text-end">
                                    {r.percent != null && Number.isFinite(r.percent) ? (
                                        <strong>{r.percent.toFixed(1)}%</strong>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td className="text-end">{fmtUa(r.turnover)}</td>
                                <td className="text-end">
                                    {r.sales > 0 ? fmtUa(r.avgCheck) : "—"}
                                </td>
                                <SourceDataCells row={r} rowsForOtherCheck={allForBySource} />
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
